// **React Imports
import {
  createContext,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

// **Third Party Imports
import {
  FaceLandmarker,
  FilesetResolver,
  ImageEmbedder
} from '@mediapipe/tasks-vision'
import cv from '@techstark/opencv-js'
import { toast } from 'react-toastify'

// **Component Imports
import CustomDialog from '../components/dialog'
import MeetingVideoPlayer from '../components/meeting-video-player'

// **Redux Imports
import { useSelector } from 'react-redux'

// **API Imports
import {
  candidateAttemptEvents,
  // candidateAttemptEventsWithImage,
  uploadCandidateProfilePicture
} from '../axios/api/candidate'
import { saveCalibrationDetailsToDB } from '../axios/api/proctor'

// **MUI Imports
import { Box, Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { getUserDetails, showErrorToast } from '../utils/utils'
import { NO_FACE_DETECTION_BLOCK_UI_IN_MINS } from '../env/env'

export const MediapipeContext = createContext(null)

// Performance optimization constants
const PERFORMANCE_THRESHOLD_MS = 100 // Skip processing if previous frame took too long

const CalibrationModal = memo(
  ({
    open,
    video,
    title,
    showButton,
    frameStatistics,
    handleCalibrateClick
  }) => {
    const isHandleCalibrateClick = useRef(false)

    //useEffects
    useEffect(() => {
      if (!isHandleCalibrateClick.current && showButton) {
        isHandleCalibrateClick.current = true
        handleCalibrateClick()
      }
    }, [showButton, handleCalibrateClick])

    return (
      <CustomDialog
        open={open}
        outsideClikClose={false}
        title={title || 'Calibrate Camera'}
        dialogPaperStyle={{ '& .MuiDialogTitle-root': { padding: '1rem' } }}
      >
        {open && (
          <Box sx={{ gap: '16px', display: 'flex', flexDirection: 'column' }}>
            <Box>
              <MeetingVideoPlayer videoStream={video} />
            </Box>
            {frameStatistics?.inPitch == null &&
              frameStatistics?.inRoll == null &&
              frameStatistics?.inYaw == null && (
                <Typography sx={{ fontWeight: '600' }} color={'error'}>
                  {`Face not detected. Please make sure the camera shutter is open and your face is centered in the frame.`}
                </Typography>
              )}
            {!showButton && (
              <>
                {(frameStatistics?.inRoll < -5 ||
                  frameStatistics?.inRoll > 5) && (
                  <Typography sx={{ fontWeight: '600' }} color={'error'}>
                    {`Tilt your face towards ${
                      frameStatistics?.inRoll < 0 ? 'left' : 'right'
                    } to calibrate`}
                  </Typography>
                )}
                {(frameStatistics?.inYaw < -5 ||
                  frameStatistics?.inYaw > 5) && (
                  <Typography sx={{ fontWeight: '600' }} color={'error'}>
                    {`Turn your face towards ${
                      frameStatistics?.inYaw > 0 ? 'left' : 'right'
                    } to calibrate`}
                  </Typography>
                )}
              </>
            )}
          </Box>
        )}
      </CustomDialog>
    )
  }
)

const MediapipeProvider = ({ children }) => {
  // Hooks
  const [searchParams] = useSearchParams()

  // Variables
  const MP_IRIS_LANDMARKS = useMemo(
    () => ({
      left: [473, 476, 474, 475, 476],
      right: [468, 471, 469, 470, 472]
    }),
    []
  )
  const normalizedFocalLength = useMemo(() => 1.28, [])

  const attemptId = useMemo(() => {
    return searchParams.get('attemptId') || null
  }, [searchParams])

  // Redux State
  const stageDetails = useSelector(
    (state) => state.candidate.contestStageDetails
  )
  const userDetails = useSelector((state) => state.auth.userData)

  // Refs
  const videoRef = useRef(null)
  const noFaceDetectedCount = useRef(0)
  const isScriptLoaded = useRef(false)
  const videoElementRef = useRef(null)
  const embededImageResultRef = useRef(null)
  const faceLandmarkerRef = useRef(null)
  const imageEmbedderRef = useRef(null)
  const imageEmbedderRunningMode = useRef('VIDEO')
  const focalLengthRef = useRef(480 * normalizedFocalLength)
  const calibratedFrameRef = useRef({
    data: [],
    inRoll: null,
    inPitch: null,
    inYaw: null
  })

  // Performance optimization refs
  const lastProcessingTimeRef = useRef(0)
  const frameCountRef = useRef(0)
  const lastFaceDetectionTimeRef = useRef(0)
  const isProcessingRef = useRef(false)
  const opencvMatricesRef = useRef([]) // Track OpenCV matrices for cleanup
  const hiddenCanvasRef = useRef(null)
  const screenShareCanvasRef = useRef(null)

  // State
  const [showCanvas, setShowCanvas] = useState({
    webcam: false,
    screenShare: false
  })
  const [calibrationDetails, setCalibrationDetails] = useState(null)
  const [mainFrame, setMainFrame] = useState({
    data: [],
    inRoll: null,
    inPitch: null,
    inYaw: null
  })
  const [scriptLoaded, setScriptLoaded] = useState({
    cvLoaded: false,
    mediaPipeLoaded: false
  })
  const [calibrationModal, setCalibrationModal] = useState({
    open: false,
    title: null,
    showButton: false
  })

  // Helper Function
  Math.degrees = (radians) => {
    return radians * (180 / Math.PI)
  }

  // Performance monitoring
  const logPerformance = useCallback((operation, duration) => {
    if (duration > PERFORMANCE_THRESHOLD_MS) {
      console.warn(`Slow operation: ${operation} took ${duration}ms`)
    }
  }, [])

  // Cleanup OpenCV matrices
  const cleanupOpenCVMatrices = useCallback(() => {
    opencvMatricesRef.current.forEach((matrix) => {
      if (matrix && !matrix.isDeleted()) {
        matrix.delete()
      }
    })
    opencvMatricesRef.current = []
  }, [])

  // Track OpenCV matrix for cleanup
  const trackMatrix = useCallback((matrix) => {
    if (matrix) {
      opencvMatricesRef.current.push(matrix)
    }
    return matrix
  }, [])

  // Handlers
  // eslint-disable-next-line  no-unused-vars
  const sendCandidateEvent = useCallback(
    (event, eventDetails) => {
      if (attemptId) {
        const payload = {
          event,
          attemptId,
          eventDetails
        }
        candidateAttemptEvents(payload)
      }
    },
    [attemptId]
  )

  // const sendCandidateEventWithImage = useCallback(
  //   async (event, eventDetails = {}, imageData) => {
  //     if (!attemptId) return

  //     const formData = new FormData()

  //     try {
  //       if (imageData) {
  //         let blob = null
  //         if (imageData instanceof Blob) {
  //           blob = imageData
  //         } else if (typeof imageData === 'string') {
  //           if (imageData.startsWith('data:')) {
  //             const res = await fetch(imageData)
  //             blob = await res.blob()
  //           }
  //         }

  //         if (blob) {
  //           const filename = `proof-${Date.now()}.jpg`
  //           formData.append('image', blob, filename)
  //         }
  //       }
  //     } catch (e) {
  //       formData.append('image', null)
  //     }

  //     if (
  //       typeof eventDetails === 'object' &&
  //       !Array.isArray(eventDetails) &&
  //       Object.keys(eventDetails || {}).length > 0
  //     ) {
  //       Object.entries(eventDetails).forEach(([key, value]) => {
  //         const isPlain =
  //           value === null ||
  //           ['string', 'number', 'boolean'].includes(typeof value)
  //         formData.append(key, isPlain ? value : JSON.stringify(value))
  //       })
  //     }

  //     candidateAttemptEventsWithImage(attemptId, event, formData)
  //   },
  //   [attemptId]
  // )

  const saveCalibrationDetails = useCallback(async (payload) => {
    const response = await saveCalibrationDetailsToDB(payload)

    if (response?.isError) {
      showErrorToast(response)
    } else {
      toast.success('Calibrated Successfully')
    }
  }, [])

  const initializeFaceLandmarker = useCallback(async () => {
    try {
      const filesetResolver = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.3/wasm'
      )
      const landmarker = await FaceLandmarker.createFromOptions(
        filesetResolver,
        {
          baseOptions: {
            modelAssetPath:
              'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
            delegate: 'GPU'
          },
          outputFaceBlendshapes: true,
          runningMode: 'VIDEO',
          numFaces: 1
        }
      )

      const imageEmbedder = await ImageEmbedder.createFromOptions(
        filesetResolver,
        {
          baseOptions: {
            modelAssetPath:
              'https://storage.googleapis.com/mediapipe-models/image_embedder/mobilenet_v3_small/float32/1/mobilenet_v3_small.tflite'
          },
          l2Normalize: true,
          quantize: true,
          runningMode: 'VIDEO'
        }
      )

      faceLandmarkerRef.current = landmarker
      imageEmbedderRef.current = imageEmbedder
      imageEmbedderRunningMode.current = 'VIDEO'

      setScriptLoaded((prev) => ({ ...prev, mediaPipeLoaded: true }))
    } catch (error) {
      setScriptLoaded((prev) => ({ ...prev, mediaPipeLoaded: false }))
    }
  }, [])

  const captureImageFrame = useCallback((videoElement) => {
    // Create a canvas element
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    // Set canvas dimensions to match the video dimensions
    canvas.width = videoElement.videoWidth
    canvas.height = videoElement.videoHeight

    // Draw the current video frame onto the canvas
    context.drawImage(videoElement, 0, 0, canvas.width, canvas.height)

    // Return the canvas element
    return canvas
  }, [])

  const captureAndEmbedImage = useCallback(
    async (videoElement, imgSrc) => {
      // Capture the image frame from the video
      let canvas
      if (videoElement) {
        canvas = captureImageFrame(videoElement)
      }

      // Create an image element
      const img = new Image()
      img.src = imgSrc || canvas.toDataURL('image/jpeg')

      // Wait for the image to load
      await new Promise((resolve) => (img.onload = resolve))

      if (imageEmbedderRunningMode.current !== 'IMAGE') {
        imageEmbedderRunningMode.current = 'IMAGE'
        await imageEmbedderRef.current.setOptions({
          runningMode: 'IMAGE'
        })
      }

      // Use the image with imageEmbedder
      const embedResult = await imageEmbedderRef.current.embed(img)

      // Do something with the result
      return { embedResult, imgSrc: imgSrc || canvas.toDataURL('image/jpeg') }
    },
    [captureImageFrame]
  )

  const intialiseMediapipe = useCallback(() => {
    if (!isScriptLoaded.current) {
      // loadOpenCV()
      initializeFaceLandmarker()
    }
  }, [initializeFaceLandmarker])

  const intialiseCalibrationDetails = useCallback(
    async (calibrationDetails) => {
      if (calibrationDetails) {
        calibratedFrameRef.current =
          calibrationDetails?.calibratedFrameDetails || null

        const { embedResult } = await captureAndEmbedImage(
          null,
          calibrationDetails?.imgSrc
        )

        embededImageResultRef.current = embedResult
      }
    },
    [captureAndEmbedImage]
  )

  const intialiseVideoElement = useCallback((stream) => {
    if (!videoElementRef.current) return

    videoRef.current = stream
    videoElementRef.current.srcObject = stream
  }, [])

  const transposeArray = useCallback((arr) => {
    return arr[0].map((_, colIndex) => arr.map((row) => row[colIndex]))
  }, [])

  const meanArray = useCallback((arr) => {
    return arr.map((col) => col.reduce((sum, val) => sum + val, 0) / col.length)
  }, [])

  const calculateStraightMeasure = useCallback(
    (straightMeasure) => {
      const transposed = transposeArray(straightMeasure)
      const meanValues = meanArray(transposed)
      return meanValues
    },
    [transposeArray, meanArray]
  )

  const base64ToFormData = useCallback(async (base64) => {
    // Split the base64 string into its components
    const [header, base64Data] = base64.split(',')

    // Extract the MIME type from the header
    const mimeTypeMatch = header.match(/data:(.*);base64/)
    const mimeType = mimeTypeMatch
      ? mimeTypeMatch[1]
      : 'application/octet-stream'

    // Decode base64 string to binary
    const binary = atob(base64Data)
    const binaryLen = binary.length
    const bytes = new Uint8Array(binaryLen)

    for (let i = 0; i < binaryLen; i++) {
      bytes[i] = binary.charCodeAt(i)
    }

    // Create a Blob from the binary data
    const blob = new Blob([bytes], { type: mimeType })

    // Create a FormData object and append the Blob
    const formData = new FormData()
    formData.append(
      'file',
      blob,
      `${getUserDetails()?.sub || 'candidate-email'}.jpeg`
    )

    return formData
  }, [])

  const handleCalibrateCamera = useCallback(async () => {
    if (!videoElementRef.current) return

    const meanArr = calculateStraightMeasure(mainFrame?.data)
    const { embedResult: result, imgSrc } = await captureAndEmbedImage(
      videoElementRef.current
    )
    embededImageResultRef.current = result

    calibratedFrameRef.current = {
      ...mainFrame,
      data: meanArr
    }

    const movementDetections = {
      calibratedFrameDetails: calibratedFrameRef.current,
      imgSrc
    }

    await saveCalibrationDetails({
      contestId: stageDetails?.contestId,
      candidateId: userDetails?.id,
      movementDetections
    })

    if (window.eventEmitter) {
      window.eventEmitter.emit('webcamCalibrated', movementDetections)
    }

    // uploading profile picture
    if (imgSrc) {
      const payload = await base64ToFormData(imgSrc)
      await uploadCandidateProfilePicture(payload)
    }

    setCalibrationModal({ open: false, title: null, showButton: false })
  }, [
    mainFrame,
    userDetails?.id,
    captureAndEmbedImage,
    saveCalibrationDetails,
    stageDetails?.contestId,
    calculateStraightMeasure,
    base64ToFormData
  ])

  const getPpc = useCallback((frameWidth, frameHeight) => {
    const wToH = frameWidth / frameHeight
    const hInCm = Math.sqrt(Math.pow(38.1, 2) / (1 + Math.pow(wToH, 2)))
    const wInCm = wToH * hInCm

    return frameWidth / wInCm
  }, [])

  const scaleLandmarks = useCallback((landmarks, frameWidth, frameHeight) => {
    if (landmarks?.length > 0) {
      return landmarks.map((landmark) => [
        landmark.x * frameWidth,
        landmark.y * frameHeight
      ])
    } else {
      return []
    }
  }, [])

  const getCamToUserDistanceCm = useCallback(
    (irisCenters, frameWidth, frameHeight) => {
      const vdistanceBetnEyes = Math.abs(
        (irisCenters?.[0]?.[0] || 0) - (irisCenters?.[1]?.[0] || 0)
      )

      return (
        ((70 / vdistanceBetnEyes) * Math.min(frameHeight, frameWidth) * 1.4) /
        10
      )?.toFixed(2)
    },
    []
  )

  const getTopPoints = useCallback((faceLandmarks) => {
    const LEFT_EYEBROW = [276, 282, 283, 285, 293, 295, 296, 300, 334, 336]
    const RIGHT_EYEBROW = [46, 52, 53, 55, 63, 65, 66, 70, 105, 107]

    const left = {}
    const right = {}

    LEFT_EYEBROW.forEach((index) => {
      const [x, y] = faceLandmarks[index]
      left[index] = [x, y]
    })

    RIGHT_EYEBROW.forEach((index) => {
      const [x, y] = faceLandmarks[index]
      right[index] = [x, y]
    })

    const leftPoint = Object.values(left).reduce(
      (max, point) => (point[0] > max[0] ? point : max),
      [-Infinity, -Infinity]
    )
    const rightPoint = Object.values(right).reduce(
      (max, point) => (point[0] > max[0] ? point : max),
      [-Infinity, -Infinity]
    )

    return [leftPoint, rightPoint]
  }, [])

  const getFrameSpace = useCallback(
    (faceLandmarks, EYEBROW_TOPS, ppc, frameWidth) => {
      const LEFT_EYE_H_EDGES = [263, 362]
      const RIGHT_EYE_H_EDGES = [133, 33]

      // Extract landmarks for the left and right eye edges
      const leftEyeEdge = [
        faceLandmarks?.[LEFT_EYE_H_EDGES?.[0]],
        faceLandmarks?.[LEFT_EYE_H_EDGES?.[1]]
      ]

      const rightEyeEdge = [
        faceLandmarks?.[RIGHT_EYE_H_EDGES?.[0]],
        faceLandmarks?.[RIGHT_EYE_H_EDGES?.[1]]
      ]

      // Extract eyebrow tops
      const eLandmarks = EYEBROW_TOPS

      // Calculate the average top distance
      const topDist = (eLandmarks[0][1] / ppc + eLandmarks[1][1] / ppc) / 2

      // Return calculated values
      return [
        (frameWidth - leftEyeEdge[0][0]) / ppc,
        rightEyeEdge[1][0] / ppc,
        topDist,
        [Math.floor(leftEyeEdge[0][0]), Math.floor(leftEyeEdge[0][1])],
        [Math.floor(rightEyeEdge[1][0]), Math.floor(rightEyeEdge[1][1])]
      ]
    },
    []
  )

  const getFaceRpy = useCallback(
    async (faceLandmarks, frameWidth, frameHeight) => {
      const startTime = performance.now()

      try {
        let x, y, z
        let roll = 0
        let pitch = 0
        let yaw = 0

        // Define the real world face points
        const points = [1, 33, 263, 61, 291, 199]
        const face_2d = points.reduce((acc, obj) => {
          acc.push(faceLandmarks?.[obj]?.[0])
          acc.push(faceLandmarks?.[obj]?.[1])
          return acc
        }, [])

        if (face_2d.length === 0) {
          return [null, null, null]
        }

        // Pre-define constants to avoid recalculation
        const k1 = 0.1318020374
        const k2 = -0.1550007612
        const p1 = -0.0071350401
        const p2 = -0.0096747708
        const s = 0
        const cx = frameWidth / 2
        const cy = frameHeight / 2

        // Create matrices with tracking for cleanup
        const dist_matrix = trackMatrix(
          cv.matFromArray(4, 1, cv.CV_64FC1, [k1, k2, p1, p2])
        )
        const cam_matrix = trackMatrix(
          cv.matFromArray(3, 3, cv.CV_64FC1, [
            focalLengthRef.current,
            s,
            cx,
            0,
            focalLengthRef.current,
            cy,
            0,
            0,
            1
          ])
        )

        const realWorldFacePoints = [
          0,
          -1.126865,
          7.475604, // nose 1
          -4.445859,
          2.663991,
          3.173422, //left eye corner 33
          4.445859,
          2.663991,
          3.173422, //right eye corner 263
          -2.456206,
          -4.342621,
          4.283884, // left mouth corner 61
          2.456206,
          -4.342621,
          4.283884, // right mouth corner 291
          0,
          -9.403378,
          4.264492
        ]

        const rvec = trackMatrix(new cv.Mat())
        const tvec = trackMatrix(new cv.Mat())

        const numRows = points.length
        const imagePoints = trackMatrix(
          cv.matFromArray(numRows, 2, cv.CV_64FC1, face_2d)
        )
        const modelPointsObj = trackMatrix(
          cv.matFromArray(6, 3, cv.CV_64FC1, realWorldFacePoints)
        )

        const success = cv.solvePnP(
          modelPointsObj,
          imagePoints,
          cam_matrix,
          dist_matrix,
          rvec,
          tvec,
          false,
          cv.SOLVEPNP_ITERATIVE
        )

        if (success) {
          const rmat = trackMatrix(cv.Mat.zeros(3, 3, cv.CV_64FC1))
          const jaco = trackMatrix(new cv.Mat())
          cv.Rodrigues(rvec, rmat, jaco)

          const sy = Math.sqrt(
            rmat.data64F[0] * rmat.data64F[0] +
              rmat.data64F[3] * rmat.data64F[3]
          )
          const singular = sy < 1e-6

          if (!singular) {
            x = Math.atan2(rmat.data64F[7], rmat.data64F[8])
            y = Math.atan2(-rmat.data64F[6], sy)
            z = Math.atan2(rmat.data64F[3], rmat.data64F[0])
          } else {
            x = Math.atan2(-rmat.data64F[5], rmat.data64F[4])
            y = Math.atan2(-rmat.data64F[6], sy)
            z = 0
          }

          roll = parseFloat(z?.toFixed(2))
          pitch = parseFloat(x?.toFixed(2))
          yaw = parseFloat(y?.toFixed(2))

          const duration = performance.now() - startTime
          logPerformance('getFaceRpy', duration)

          return [roll, pitch, yaw]
        } else {
          return [null, null, null]
        }
      } catch (error) {
        console.error('Error in getFaceRpy:', error)
        return [null, null, null]
      } finally {
        // Cleanup matrices after processing
        cleanupOpenCVMatrices()
      }
    },
    [trackMatrix, cleanupOpenCVMatrices, logPerformance]
  )

  const getIrisDistanceFromEdge = useCallback(
    (faceLandmarks, EYEBROW_TOPS, irisCenters) => {
      const LEFT_EYE_H_EDGES = [263, 362]
      const RIGHT_EYE_H_EDGES = [133, 33]
      const LEFT_EYE_V_EDGES = [386, 374]
      const RIGHT_EYE_V_EDGES = [159, 145]

      const leftPoints = [
        [
          faceLandmarks[LEFT_EYE_H_EDGES[0]],
          faceLandmarks[LEFT_EYE_H_EDGES[1]]
        ],
        [faceLandmarks[LEFT_EYE_V_EDGES[0]], faceLandmarks[LEFT_EYE_V_EDGES[1]]]
      ]

      const rightPoints = [
        [
          faceLandmarks[RIGHT_EYE_H_EDGES[0]],
          faceLandmarks[RIGHT_EYE_H_EDGES[1]]
        ],
        [
          faceLandmarks[RIGHT_EYE_V_EDGES[0]],
          faceLandmarks[RIGHT_EYE_V_EDGES[1]]
        ]
      ]

      const topPoints = EYEBROW_TOPS

      const getDistanceFromIris = (irisCenters, point, eye) => {
        const [leftIrisCenter, rightIrisCenter] = irisCenters
        let irisCenter

        if (eye === 'left') {
          irisCenter = leftIrisCenter
        } else {
          irisCenter = rightIrisCenter
        }

        const dx = irisCenter[0] - point[0]
        const dy = irisCenter[1] - point[1]
        return Math.sqrt(dx * dx + dy * dy)
      }

      const leftDist = []
      leftPoints.forEach((point) => {
        leftDist.push(
          parseFloat(
            getDistanceFromIris(irisCenters, point[0], 'left').toFixed(3)
          )
        )
        leftDist.push(
          parseFloat(
            getDistanceFromIris(irisCenters, point[1], 'left').toFixed(3)
          )
        )
      })

      const rightDist = []
      rightPoints.forEach((point) => {
        rightDist.push(
          parseFloat(
            getDistanceFromIris(irisCenters, point[0], 'right').toFixed(3)
          )
        )
        rightDist.push(
          parseFloat(
            getDistanceFromIris(irisCenters, point[1], 'right').toFixed(3)
          )
        )
      })

      const topDist = []
      topDist.push(
        parseFloat(
          getDistanceFromIris(irisCenters, topPoints[0], 'left').toFixed(3)
        )
      )
      topDist.push(
        parseFloat(
          getDistanceFromIris(irisCenters, topPoints[1], 'right').toFixed(3)
        )
      )

      return [leftDist, rightDist, topDist]
    },
    []
  )

  const getDirection = useCallback(
    (
      leftDist,
      rightDist,
      leftIeyeEdgeDist,
      rightIeyeEdgeDist,
      topIeyeDist,
      distance,
      liRadius,
      riRadius,
      roll,
      pitch,
      yaw,
      ppc,
      frameWidth
    ) => {
      const initFrameSizeAbove = 300
      const maxeVerticalMovement = 3.5
      const straightMeasure = calibratedFrameRef?.current?.data || []
      // const inRoll = parseFloat(calibratedFrameRef?.current?.inRoll || 0)
      const inPitch = parseFloat(calibratedFrameRef?.current?.inPitch || 0)
      const inYaw = parseFloat(calibratedFrameRef?.current?.inYaw || 0)

      const ltChange =
        straightMeasure[2] +
        straightMeasure[3] -
        (leftIeyeEdgeDist[2] + leftIeyeEdgeDist[3])
      const rtChange =
        straightMeasure[7] +
        straightMeasure[8] -
        (rightIeyeEdgeDist[2] + rightIeyeEdgeDist[3])
      // const llMaxMovement = leftIeyeEdgeDist[1] - liRadius
      const lrMaxMovement = leftIeyeEdgeDist[0] - liRadius
      const rlMaxMovement = rightIeyeEdgeDist[1] - riRadius
      // const rrMaxMovement = rightIeyeEdgeDist[0] - riRadius

      // const ltE = straightMeasure[10] - topIeyeDist[0]
      // const rtE = straightMeasure[11] - topIeyeDist[1]
      // const llE = straightMeasure[1] - leftIeyeEdgeDist[1]
      const lrE = straightMeasure[0] - leftIeyeEdgeDist[0]
      const ltE2 = straightMeasure[2] - leftIeyeEdgeDist[2]
      // const ldE = straightMeasure[3] - leftIeyeEdgeDist[3]
      const rlE = straightMeasure[6] - rightIeyeEdgeDist[1]
      // const rrE = straightMeasure[5] - rightIeyeEdgeDist[0]
      // const rtE2 = straightMeasure[7] - rightIeyeEdgeDist[2]
      // const rdE = straightMeasure[8] - rightIeyeEdgeDist[3]

      const teGoodAngle = Math.degrees(
        Math.atan(initFrameSizeAbove / ppc / distance)
      )
      const lerGoodAngle = Math.degrees(
        Math.atan((frameWidth / ppc - leftDist) / distance)
      )
      const relGoodAngle = Math.degrees(
        Math.atan((frameWidth / ppc - rightDist) / distance)
      )

      // const rollOffset = inRoll - roll
      const pitchOffset = inPitch - pitch
      const yawOffset = inYaw - yaw

      if (pitchOffset < -20) {
        return 'Down'
      }
      if (pitchOffset > 25 && ltChange > 4 && rtChange > 3) {
        return 'Top'
      }

      if (Math.abs(yawOffset) <= 10 && Math.abs(pitchOffset) < 10) {
        if (
          Math.abs(rlE / (rlMaxMovement + rlE)) > relGoodAngle / 30 &&
          rlE < 0
        ) {
          return 'Left'
        } else if (
          Math.abs(lrE / (lrMaxMovement + lrE)) > lerGoodAngle / 30 &&
          lrE < 0
        ) {
          return 'Right'
        } else if (
          Math.abs(ltE2 / maxeVerticalMovement) > teGoodAngle / 30 &&
          ltE2 < 0
        ) {
          return 'Top'
        } else {
          if (ltChange >= -3 && rtChange >= -3) {
            return 'in Screen'
          } else {
            return 'Top'
          }
        }
      } else if (Math.abs(yawOffset) < 30) {
        if (
          Math.abs(rlE / (rlMaxMovement + rlE)) >
            (relGoodAngle - Math.abs(yawOffset)) / 30 &&
          rlE < 0 &&
          yawOffset > 0
        ) {
          return 'Left'
        } else if (
          Math.abs(lrE / (lrMaxMovement + lrE)) >
            (lerGoodAngle - Math.abs(yawOffset)) / 30 &&
          lrE < 0 &&
          yawOffset < 0
        ) {
          return 'Right'
        } else {
          return 'in Screen'
        }
      } else {
        if (pitchOffset > 30) {
          return 'Top'
        } else if (yawOffset > 30) {
          return 'Left'
        } else if (yawOffset < -30) {
          return 'Right'
        }
      }
    },
    []
  )

  const predictWebcam = useCallback(
    async (showCalibration) => {
      // Performance throttling - skip if processing is too frequent

      const videoEle = document.getElementById('webcam-video-feed')
      const now = performance.now()

      if (showCalibration) {
        setCalibrationModal((prev) => {
          if (!prev.open) {
            return { ...prev, open: true }
          }
          return prev
        })
      }

      if (now - lastFaceDetectionTimeRef.current < 2000) {
        return
      }

      // Skip if already processing to prevent overlapping calls
      if (isProcessingRef.current) {
        return
      }

      // Skip if previous frame took too long
      if (now - lastProcessingTimeRef.current < PERFORMANCE_THRESHOLD_MS) {
        return
      }

      if (!faceLandmarkerRef.current || !videoEle) {
        return
      }

      isProcessingRef.current = true
      const startTime = performance.now()
      lastFaceDetectionTimeRef.current = now

      try {
        const video = videoEle
        const videoWidth = video.videoWidth
        const videoHeight = video.videoHeight

        if (videoWidth === 0 || videoHeight === 0) {
          return
        }

        focalLengthRef.current = videoHeight * normalizedFocalLength

        const startTimeMs = performance.now()
        const results = await faceLandmarkerRef.current.detectForVideo(
          video,
          startTimeMs
        )

        if (results?.faceLandmarks?.length > 0) {
          if (!showCalibration) {
            noFaceDetectedCount.current = 0
            setCalibrationModal((prev) => ({
              ...prev,
              title: null,
              open: false
            }))
          }

          for (const landmarks of results.faceLandmarks) {
            const newFaceMark = scaleLandmarks(
              landmarks,
              videoWidth,
              videoHeight
            )
            const lIrisPoints = [
              newFaceMark?.[MP_IRIS_LANDMARKS?.left[0]] || [0, 0],
              newFaceMark?.[MP_IRIS_LANDMARKS?.left[1]] || [0, 0],
              newFaceMark?.[MP_IRIS_LANDMARKS?.left[2]] || [0, 0],
              newFaceMark?.[MP_IRIS_LANDMARKS?.left[3]] || [0, 0],
              newFaceMark?.[MP_IRIS_LANDMARKS?.left[4]] || [0, 0]
            ]
            const rIrisPoints = [
              newFaceMark?.[MP_IRIS_LANDMARKS?.right[0]] || [0, 0],
              newFaceMark?.[MP_IRIS_LANDMARKS?.right[1]] || [0, 0],
              newFaceMark?.[MP_IRIS_LANDMARKS?.right[2]] || [0, 0],
              newFaceMark?.[MP_IRIS_LANDMARKS?.right[3]] || [0, 0],
              newFaceMark?.[MP_IRIS_LANDMARKS?.right[4]] || [0, 0]
            ]
            const irisCenters = [lIrisPoints[0], rIrisPoints[0]]

            // Optimize OpenCV operations
            const lMatPoints = trackMatrix(
              cv.matFromArray(
                lIrisPoints.length - 1,
                1,
                cv.CV_32SC2,
                lIrisPoints
                  ?.slice(1)
                  ?.flat()
                  ?.map((point) => Math.floor(point))
              )
            )
            const rMatPoints = trackMatrix(
              cv.matFromArray(
                rIrisPoints.length - 1,
                1,
                cv.CV_32SC2,
                rIrisPoints
                  ?.slice(1)
                  ?.flat()
                  ?.map((point) => Math.floor(point))
              )
            )

            const { radius: lRadius } = cv.minEnclosingCircle(lMatPoints)
            const { radius: rRadius } = cv.minEnclosingCircle(rMatPoints)

            const leArea = (3.142 * Math.pow(lRadius, 2)).toFixed(3)
            const reArea = (3.142 * Math.pow(rRadius, 2)).toFixed(3)

            const EYEBROW_TOPS = getTopPoints(newFaceMark)
            const userDistance = getCamToUserDistanceCm(
              irisCenters,
              videoWidth,
              videoHeight
            )
            const ppc = getPpc(videoWidth, videoHeight)
            const [leftDistance, rightDistance] = getFrameSpace(
              newFaceMark,
              EYEBROW_TOPS,
              ppc,
              videoWidth
            )

            const [roll, pitch, yaw] = await getFaceRpy(
              newFaceMark,
              videoWidth,
              videoHeight
            )

            const [leftIeyeEdgeDist, rightIeyeEdgeDist, topIeyeDist] =
              getIrisDistanceFromEdge(newFaceMark, EYEBROW_TOPS, irisCenters)

            leftIeyeEdgeDist.push(parseFloat(leArea))
            rightIeyeEdgeDist.push(parseFloat(reArea))

            if (roll !== null && pitch !== null && yaw !== null) {
              const rollValue = parseFloat(
                (180.0 * (roll / Math.PI)).toFixed(2)
              )
              const pitchValue = parseFloat(
                (180.0 * (pitch / Math.PI)).toFixed(2)
              )
              const yawValue = parseFloat((180.0 * (yaw / Math.PI)).toFixed(2))

              const absoluteValue =
                pitchValue > 0
                  ? (pitchValue - 180).toFixed(2)
                  : (pitchValue + 180).toFixed(2)

              if (calibratedFrameRef?.current?.data?.length === 0) {
                // Batch state updates
                const updates = {
                  inRoll: rollValue,
                  inPitch: absoluteValue,
                  inYaw: yawValue
                }

                if (
                  rollValue < 5 &&
                  rollValue > -5 &&
                  yawValue < 5 &&
                  yawValue > -5 &&
                  results?.faceLandmarks?.length === 1
                ) {
                  const newArr = [
                    ...leftIeyeEdgeDist,
                    ...rightIeyeEdgeDist,
                    ...topIeyeDist
                  ]
                  updates.data = [...(mainFrame.data || []), newArr]
                  setCalibrationModal((prev) => ({ ...prev, showButton: true }))
                } else {
                  setCalibrationModal((prev) => ({
                    ...prev,
                    showButton: false
                  }))
                }

                setMainFrame((prev) => ({ ...prev, ...updates }))
              } else {
                const direction = getDirection(
                  leftDistance,
                  rightDistance,
                  leftIeyeEdgeDist,
                  rightIeyeEdgeDist,
                  topIeyeDist,
                  userDistance,
                  lRadius,
                  rRadius,
                  rollValue,
                  absoluteValue,
                  yawValue,
                  ppc,
                  videoWidth
                )

                // eslint-disable-next-line  no-unused-vars
                const newDirection = direction?.toLowerCase()

                // if (
                //   !showCalibration &&
                //   newDirection &&
                //   newDirection !== 'in screen'
                // ) {
                //   sendCandidateEvent(
                //     newDirection === 'right'
                //       ? 'lookingRight'
                //       : newDirection === 'left'
                //       ? 'lookingLeft'
                //       : newDirection === 'top'
                //       ? 'lookingTop'
                //       : 'lookingBottom',
                //     { timestamp: new Date().toISOString() }
                //   )
                // }

                // Optimize image embedding - only run if enabled and every few frames
                frameCountRef.current++
                if (frameCountRef.current % 10 === 0) {
                  try {
                    if (imageEmbedderRunningMode.current !== 'VIDEO') {
                      imageEmbedderRunningMode.current = 'VIDEO'
                      await imageEmbedderRef.current.setOptions({
                        runningMode: 'VIDEO'
                      })
                    }

                    // const embedderResult =
                    //   await imageEmbedderRef.current.embedForVideo(
                    //     video,
                    //     performance.now()
                    //   )

                    // Only calculate similarity if needed
                    // const similarity = ImageEmbedder.cosineSimilarity(
                    //   embededImageResultRef.current.embeddings[0],
                    //   embedderResult.embeddings[0]
                    // )
                  } catch (error) {
                    console.warn('Image embedding failed:', error)
                  }
                }
              }
            }
          }
        } else if (!showCalibration) {
          if (
            noFaceDetectedCount.current <
            NO_FACE_DETECTION_BLOCK_UI_IN_MINS * 60
          ) {
            noFaceDetectedCount.current = noFaceDetectedCount.current + 5
          } else {
            setCalibrationModal((prev) => {
              if (!prev.open) {
                return { ...prev, open: true, title: 'No Face Detected' }
              }
              return prev
            })
          }
          setMainFrame({ data: [], inPitch: null, inRoll: null, inYaw: null })
          // try {
          //   const video = videoEle
          //   const canvas = hiddenCanvasRef.current
          //   if (video && canvas) {
          //     canvas.width = video.videoWidth
          //     canvas.height = video.videoHeight
          //     const ctx = canvas.getContext('2d')
          //     ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

          //     // Prefer Blob to avoid base64 data URLs
          //     canvas.toBlob(
          //       (blob) => {
          //         if (blob) {
          //           sendCandidateEventWithImage('noFaceDetected', {}, blob)
          //         }
          //       },
          //       'image/jpeg',
          //       0.7
          //     )
          //   }
          // } catch (err) {
          //   console.error('Failed to save frame as image:', err)
          // }
        } else {
          setMainFrame({ data: [], inPitch: null, inRoll: null, inYaw: null })
        }

        const totalDuration = performance.now() - startTime
        lastProcessingTimeRef.current = performance.now()
        logPerformance('predictWebcam total', totalDuration)
      } catch (error) {
        console.error('Error in predictWebcam:', error)
      } finally {
        isProcessingRef.current = false
        cleanupOpenCVMatrices()
      }
    },
    [
      getPpc,
      getFaceRpy,
      getTopPoints,
      getDirection,
      getFrameSpace,
      scaleLandmarks,
      MP_IRIS_LANDMARKS,
      // sendCandidateEventWithImage,
      normalizedFocalLength,
      getCamToUserDistanceCm,
      getIrisDistanceFromEdge,
      trackMatrix,
      cleanupOpenCVMatrices,
      logPerformance,
      mainFrame.data
    ]
  )

  const cleanUpResource = useCallback(() => {
    // Stop and release any media streams
    const stopMediaStream = (stream) => {
      try {
        if (stream && typeof stream.getTracks === 'function') {
          stream.getTracks().forEach((t) => {
            try {
              t.stop()
            } catch (e) {
              // ignore
            }
          })
        }
      } catch (e) {
        // ignore
      }
    }

    try {
      // stop stored stream reference
      stopMediaStream(videoRef.current)

      // stop stream attached to video element
      if (videoElementRef.current) {
        stopMediaStream(videoElementRef.current.srcObject)
        try {
          videoElementRef.current.srcObject = null
        } catch (e) {
          // ignore
        }
      }

      // Close mediapipe instances if possible
      const tryClose = (obj) => {
        if (!obj) return
        try {
          if (typeof obj.close === 'function') obj.close()
          else if (typeof obj.dispose === 'function') obj.dispose()
          else if (typeof obj.stop === 'function') obj.stop()
        } catch (e) {
          // ignore
        }
      }

      tryClose(faceLandmarkerRef.current)
      tryClose(imageEmbedderRef.current)

      faceLandmarkerRef.current = null
      imageEmbedderRef.current = null
      embededImageResultRef.current = null

      // Cleanup OpenCV matrices
      cleanupOpenCVMatrices()

      // Clear canvases
      try {
        if (hiddenCanvasRef.current) {
          const c = hiddenCanvasRef.current
          try {
            const ctx = c.getContext && c.getContext('2d')
            if (ctx) ctx.clearRect(0, 0, c.width, c.height)
          } catch (e) {}
          c.width = 0
          c.height = 0
        }
        if (screenShareCanvasRef.current) {
          const c2 = screenShareCanvasRef.current
          try {
            const ctx2 = c2.getContext && c2.getContext('2d')
            if (ctx2) ctx2.clearRect(0, 0, c2.width, c2.height)
          } catch (e) {}
          c2.width = 0
          c2.height = 0
        }
      } catch (e) {
        // ignore
      }

      // Reset refs/state
      videoRef.current = null
      videoElementRef.current = null
      imageEmbedderRunningMode.current = 'VIDEO'

      setScriptLoaded({ cvLoaded: false, mediaPipeLoaded: false })
    } catch (error) {
      console.error('Error during cleanUpResource:', error)
    } finally {
      frameCountRef.current = 0
      noFaceDetectedCount.current = 0
      lastProcessingTimeRef.current = 0
      lastFaceDetectionTimeRef.current = 0
      isProcessingRef.current = false
    }
  }, [cleanupOpenCVMatrices, setScriptLoaded])

  return (
    <MediapipeContext.Provider
      value={{
        scriptLoaded,
        predictWebcam,
        setShowCanvas,
        cleanUpResource,
        calibrationDetails,
        screenShareCanvasRef,
        setCalibrationDetails,
        intialiseMediapipe,
        setCalibrationModal,
        intialiseVideoElement,
        intialiseCalibrationDetails
      }}
    >
      <>
        {children}
        {showCanvas?.webcam && !calibrationDetails && (
          <video
            ref={videoElementRef}
            style={{
              visibility: 'hidden',
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%,-50%)',
              zIndex: '-1'
            }}
            onContextMenu={(e) => {
              e.preventDefault()
            }}
            muted
            autoPlay
            playsInline
            disablePictureInPicture={true}
          />
        )}

        {showCanvas?.webcam && (
          <canvas ref={hiddenCanvasRef} style={{ display: 'none' }} />
        )}
        {showCanvas?.screenShare && (
          <canvas ref={screenShareCanvasRef} style={{ display: 'none' }} />
        )}

        <CalibrationModal
          video={videoRef.current}
          title={calibrationModal?.title}
          frameStatistics={mainFrame}
          open={calibrationModal.open}
          showButton={calibrationModal.showButton}
          handleCalibrateClick={handleCalibrateCamera}
          handleClose={() => {
            setCalibrationModal({ open: false, title: null, showButton: false })
          }}
        />
      </>
    </MediapipeContext.Provider>
  )
}

export default MediapipeProvider
